import { UpdateProfileStatus, useCustomerDetails, useUpdateSuitability, type UpdateSuitabilityInput } from '@egw/api'
import { Button, Collapse, Field, Input, ModalDrawer, RadioGroup, useModalState } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { useEffect, useState, type FC } from 'react'
import { Controller, useForm } from 'react-hook-form'

const log = new Logger('Edit Politically Exposed Person')

interface PepForm {
  isPoliticallyExposedPerson: boolean | number | undefined
  affiliatedPoliticalOrganizations: string | undefined
  hasPoliticallyExposedImmediateFamilyMembers: boolean | number | undefined
  politicallyExposedImmediateFamilyMembers: string | undefined
}

// Compononent to Be used within a FormProvider from react-hook-form
export const EditPoliticallyExposedPerson: FC = () => {
  const { t } = useTranslation('features-settings')
  const { openModal, closeModal, isOpen } = useModalState()

  const [updateError, setUpdateError] = useState<boolean>(false)

  const { data, refetch, isFetching } = useCustomerDetails()

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors: formErrors, isValid, isSubmitting },
    reset,
  } = useForm<PepForm>({
    defaultValues: {
      isPoliticallyExposedPerson: undefined,
      affiliatedPoliticalOrganizations: undefined,
      hasPoliticallyExposedImmediateFamilyMembers: undefined,
      politicallyExposedImmediateFamilyMembers: undefined,
    },
  })

  useEffect(() => {
    if (data?.customer) {
      reset({
        isPoliticallyExposedPerson: Number(data.customer.isPoliticallyExposedPerson), // Convert to number to avoid validation error w/ Radio Input
        affiliatedPoliticalOrganizations: data.customer.affiliatedPoliticalOrganizations?.length
          ? data.customer.affiliatedPoliticalOrganizations.join(', ')
          : '',
        hasPoliticallyExposedImmediateFamilyMembers: Number(data.customer.hasPoliticallyExposedImmediateFamilyMembers),
        politicallyExposedImmediateFamilyMembers: data.customer.politicallyExposedImmediateFamilyMembers
          ? data.customer.politicallyExposedImmediateFamilyMembers.join(', ')
          : '',
      })
    }
  }, [data, reset])

  const showPep = data?.customer.isPoliticallyExposedPerson !== undefined
  const showFamilyPep = data?.customer.hasPoliticallyExposedImmediateFamilyMembers !== undefined

  const isPoliticallyExposedPersonField = watch('isPoliticallyExposedPerson')

  const hasPoliticallyExposedImmediateFamilyMemberField = watch(
    'hasPoliticallyExposedImmediateFamilyMembers',
  ) as unknown as boolean | undefined

  const onSubmit = (formData: PepForm): void => {
    setUpdateError(false)

    log.debug('Submitting Form', { formData })

    const payload: UpdateSuitabilityInput = {
      isPoliticallyExposedPerson: Boolean(formData.isPoliticallyExposedPerson),
      affiliatedPoliticalOrganizations: formData.affiliatedPoliticalOrganizations
        ? formData.affiliatedPoliticalOrganizations.split(',').map((e) => e.trimStart())
        : [], // If empty string pass an empty array otherwise split the string by comma
      hasPoliticallyExposedImmediateFamilyMembers: Boolean(formData.hasPoliticallyExposedImmediateFamilyMembers),
      politicallyExposedImmediateFamilyMembers: formData.politicallyExposedImmediateFamilyMembers
        ? formData.politicallyExposedImmediateFamilyMembers.split(',').map((e) => e.trimStart())
        : [],
    }

    void updateSuitability(payload)
  }

  const { mutate: updateSuitability, isLoading } = useUpdateSuitability({
    onSuccess: (res) => {
      if (res.__typename === 'UpdateProfileError' || res.status === UpdateProfileStatus.FAILED) {
        log.error('Error updating brokerage affiliation', { data })
        setUpdateError(true)
      } else if (res.status === UpdateProfileStatus.COMPLETE) {
        void refetch()
          .catch((error: unknown) => {
            log.error(error)
          })
          .finally(() => {
            setUpdateError(false)
            closeModal()
            reset()
          })
      }
    },
    onError: () => {
      setUpdateError(true)
    },
  })

  return (
    <>
      {showPep ? (
        <Field
          className="w-full justify-between border-list mb-0 py-4 px-2"
          valueClassName="text-text-meta typo-primary-base-light"
          value={t('politically_exposed_person.politically_exposed_person_title')}
          rightValue={isPoliticallyExposedPersonField ? t('common.yes') : t('common.no')}
          testId="editIsPoliticallyExposedPerson"
          onEdit={openModal}
        />
      ) : null}
      {showFamilyPep ? (
        <Field
          className="w-full justify-between border-list mb-0 py-4 px-2"
          valueClassName="text-text-meta typo-primary-base-light"
          value={t('politically_exposed_person.politically_exposed_person_immediate_family_title')}
          rightValue={hasPoliticallyExposedImmediateFamilyMemberField ? t('common.yes') : t('common.no')}
          testId="editHasPoliticallyExposedImmediateFamilyMember"
          onEdit={openModal}
        />
      ) : null}
      <ModalDrawer
        isOpen={isOpen}
        onClose={closeModal}
        title={t('politically_exposed_person.edit_politically_exposed_person')}
      >
        <form
          id="updateSuitabilityForm"
          className="flex flex-col h-full md:h-screen"
          onSubmit={(e) => {
            e.preventDefault()
            void handleSubmit(onSubmit)()
          }}
        >
          <div className="mb-sm">
            <p className="typo-primary-base-regular text-text-body">{t('politically_exposed_person.description')}</p>
            <Collapse
              testId="politically-exposed-person-collapse"
              className="text-text-action typo-primary-sm-semibold w-fit mt-xs"
              title={
                <div className="flex flex-row gap-3xs">
                  {t('politically_exposed_person.show_more')}{' '}
                  <InformationCircleIcon className="w-4 h-4 text-text-action" />
                </div>
              }
              chevronClassName="hidden"
            >
              <div className="typo-primary-sm-regular text-text-body bg-sky-100 p-sm">
                <p>{t('politically_exposed_person.modal_description_1')}</p>
                <ul className="list-disc list-inside ml-2">
                  <li>{t('politically_exposed_person.modal_ul_1')}</li>
                  <li>{t('politically_exposed_person.modal_ul_2')}</li>
                  <li>{t('politically_exposed_person.modal_ul_3')}</li>
                  <li>{t('politically_exposed_person.modal_ul_4')}</li>
                  <li>{t('politically_exposed_person.modal_ul_5')}</li>
                  <li>{t('politically_exposed_person.modal_ul_6')}</li>
                </ul>
                <p className="mt-md">{t('politically_exposed_person.modal_description_2')}</p>
              </div>
            </Collapse>
          </div>
          <Controller
            name="isPoliticallyExposedPerson"
            control={control}
            rules={{
              required: true,
              validate: (value) => (typeof value === 'number' && !isNaN(value)) || 'Field is required',
            }}
            render={({ field: { ref: _, ...restField } }) => (
              <RadioGroup
                {...restField}
                value={restField.value ? 1 : 0}
                options={[
                  { value: 0, label: t('common.no') },
                  { value: 1, label: t('common.yes') },
                ]}
                name="isPoliticallyExposedPerson"
                testId="is-politically-exposed-person_radio"
              />
            )}
          />
          {isPoliticallyExposedPersonField ? (
            <>
              <p className="typo-primary-sm-semibold text-text-body mb-3xs">
                {t('politically_exposed_person.is_yes_politically_exposed_person')}
              </p>
              <Controller
                name="affiliatedPoliticalOrganizations"
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder={t('politically_exposed_person.political_organizations')}
                    name="affiliatedPoliticalOrganizations"
                    testId="politically-exposed-person-company_input"
                    error={formErrors.affiliatedPoliticalOrganizations?.message}
                    isPII
                  />
                )}
              />
            </>
          ) : null}

          <p className="typo-primary-base-regular text-text-body mt-lg mb-sm">
            {t('politically_exposed_person.description_2')}
          </p>

          <Controller
            name="hasPoliticallyExposedImmediateFamilyMembers"
            control={control}
            rules={{
              required: true,
              validate: (value) => (typeof value === 'number' && !isNaN(value)) || 'Field is required',
            }}
            render={({ field: { ref: _, ...restField } }) => (
              <RadioGroup
                {...restField}
                value={restField.value ? 1 : 0}
                options={[
                  { value: 0, label: t('common.no') },
                  { value: 1, label: t('common.yes') },
                ]}
                name="hasPoliticallyExposedImmediateFamilyMembers"
                testId="is-immediate-family-politically-exposed-person_radio"
              />
            )}
          />

          {hasPoliticallyExposedImmediateFamilyMemberField ? (
            <>
              <p className="typo-primary-sm-semibold text-text-body mb-3xs">
                {t('politically_exposed_person.is_yes_politically_exposed_immediate_family')}
              </p>
              <Controller
                name="politicallyExposedImmediateFamilyMembers"
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder={t('politically_exposed_person.immediate_family_members')}
                    name="politicallyExposedImmediateFamilyMembers"
                    testId="politically-exposed-immediate-family-members_input"
                    error={formErrors.affiliatedPoliticalOrganizations?.message}
                  />
                )}
              />
            </>
          ) : null}

          <p className="typo-primary-xs-regular text-text-meta my-md">{t('politically_exposed_person.attestment')}</p>

          {updateError ? (
            <div className="flex bg-alertErrorBg p-3 rounded-md">
              <InformationCircleIcon className="w-6 h-6 fill-red-500" />
              <p className="text-sm text-alertErrorText ml-2">{t('common.update_error')} </p>
            </div>
          ) : null}
          <Button
            type="submit"
            isLoading={isLoading || isFetching}
            isDisabled={!isValid || isSubmitting}
            size="small"
            className="w-full"
            testId="primaryAction_button"
          >
            {t('common.confirm')}
          </Button>
        </form>
      </ModalDrawer>
    </>
  )
}
